/**
 * NZSC-3307: A/B 실험 플래그 플러그인.
 *
 * 기존 $experiment.run 은 실험이 "코드 동작"을 바꾸는 구조라(group==='B' 면 다른 라우트),
 * 변형을 바꾸거나 실험을 끝낼 때마다 배포가 필요했다. 여기서는 실험이 "값"만 내려주고
 * 코드는 그 값을 읽어 쓴다.
 *
 * 사용법
 *   // 1) 퍼널 진입 화면 mounted 에서 미리 받아둔다
 *   await this.$flagPrefetch(["example_feature"]);
 *
 *   // 2) 분기 시점에는 동기로 읽는다 (네트워크 대기 없음)
 *   const { enabled } = this.$flag("example_feature", { enabled: false });
 *   if (enabled) this.fnEnableFeature();
 *
 * 실험이 없거나(DRAFT), 배정 대상이 아니거나, 서버가 죽어도 기본값이 그대로 쓰인다.
 */
export default {
  install(Vue, { store, mirror } = {}) {
    if (!store) return false;

    /**
     * 실험 플래그 프리페치. 분기가 일어나기 전에 호출해야 한다.
     * @param {string[]} keys
     */
    Vue.prototype.$flagPrefetch = function (keys) {
      return store.dispatch("flagStore/acPrefetchFlags", keys);
    };

    /**
     * 플래그 값 동기 조회. 프리페치가 안 됐거나 배정 대상이 아니면 기본값을 그대로 돌려준다.
     * @param {string} key 실험 키
     * @param {object} defaultPayload 코드에 적어두는 기본값 (실험이 없을 때의 동작)
     * @returns {object} payload — 기본값에 실험 값을 덮어쓴 결과
     */
    Vue.prototype.$flag = function (key, defaultPayload = {}) {
      const flag = store.getters["flagStore/experimentFlag"](key);
      if (!flag) return { ...defaultPayload };

      // 실험군을 Mixpanel super property 로 1회 등록한다.
      // 이벤트마다 직접 박으면 반드시 어딘가 빠지는데, super property 는 등록 이후
      // 모든 이벤트에 자동으로 붙는다. web 은 자동캡처라 이 방식이라야 누락이 없다.
      fnTagExperiment(store, key, flag.variant);

      // NZSC-3582: 자체수집(CH) 공통 노출 이벤트. Mixpanel 은 super property 가 커버하지만
      // 자체수집 track_events 에는 "이 유저가 언제 어떤 변형을 실제로 사용했는지" 앵커가
      // 없어 실험별 수기 이벤트(report_qna_ab_assign)를 매번 심어야 했다. $flag 해석 시점에
      // 자동 발화해 어떤 실험이든 실험별 코드 없이 X-Ray V2 가 노출을 잡을 수 있게 한다.
      fnEmitExposure(mirror, key, flag.variant);

      // 실험 payload 에 일부 키만 있어도 나머지는 기본값이 살아있게 병합한다.
      return { ...defaultPayload, ...(flag.payload || {}) };
    };

    /** 실험 변형만 필요할 때 (분기보다는 계측/디버깅용). */
    Vue.prototype.$flagVariant = function (key) {
      return store.getters["flagStore/experimentFlag"](key)?.variant ?? null;
    };
  },
};

// 세션당 실험·변형별 1회 발화 (플러딩 방지). 저장 값 = variant_key — 세션 중 변형이
// 바뀌면(FORCED 전환·실험 재시작) 새 변형으로 재발화한다(CR W1). sessionStorage 접근이
// 막힌 환경(프라이빗 모드 등)은 페이지 로드 단위 in-memory Map 으로 폴백 — 어느 쪽이든
// 같은 변형의 무한 반복 발화는 없다.
const EXPOSURE_STORAGE_PREFIX = "nz_exp_exposure_";
const exposureFiredFallback = new Map(); // key → variant

function fnEmitExposure(mirror, key, variant) {
  if (!variant || typeof mirror !== "function") return;

  let deduped = false;
  try {
    const storageKey = EXPOSURE_STORAGE_PREFIX + key;
    if (window.sessionStorage.getItem(storageKey) === variant) return;
    window.sessionStorage.setItem(storageKey, variant);
    deduped = true;
  } catch (error) {
    // sessionStorage 불가 — in-memory 폴백
  }
  if (!deduped) {
    if (exposureFiredFallback.get(key) === variant) return;
    exposureFiredFallback.set(key, variant);
  }

  try {
    mirror("experiment_exposure", { experiment_key: key, variant_key: variant });
  } catch (error) {
    // 계측 실패가 화면 동작을 막아선 안 된다.
  }
}

function fnTagExperiment(store, key, variant) {
  if (!variant) return;
  const tagged = store.getters["flagStore/experimentTaggedKeys"];
  if (tagged.includes(key)) return;

  try {
    if (typeof window !== "undefined" && window.mixpanel?.register) {
      window.mixpanel.register({ [`exp_${key}`]: variant });
    }
  } catch (error) {
    // 계측 실패가 화면 동작을 막아선 안 된다.
  }
  store.commit("flagStore/ADD_EXPERIMENT_TAGGED_KEY", key);
}
